import React, { useCallback, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Table,
    Badge,
    Alert
} from "react-bootstrap";
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import { GRADIENTS } from "utils/theme";
import usePagination from "hooks/usePagination";
import Pagination from "components/ui/Pagination";
import TableFilter from "components/ui/TableFilter";
import { useNavigate } from "react-router-dom";
import "./MachineStatus.css";

function MachineStatus() {
    const { fetchData } = Useaxios();
    const navigate = useNavigate();

    const getInitialFilters = () => ({
        machineId: '',
        name: '',
        status: ''
    });

    const fetchMachineStatus = useCallback(async (params) => {
        const { page = 1, limit = 10, ...filters } = params;
        // console.log('Fetching with params:', { page, limit, filters });
        
        // Try dashboard API first, fallback to machines API
        let res = await fetchData({
            url: API.getMachineStatus,
            method: 'GET',
            params: { page, limit, ...filters },
            showMessage: false
        });
        return res
        
      
        // return { data: [], total: 0, counts: { total: 0, online: 0, offline: 0 } };
    }, []);

    const {
        data: _machines,
        loading,
        currentPage,
        totalPages,
        totalItems,
        pageSize,
        filters,
        goToPage,
        nextPage,
        prevPage,
        changePageSize,
        updateFilters,
        clearFilters,
        refresh,
        additionalData
    } = usePagination(fetchMachineStatus, getInitialFilters());
    // console.log(_machines)
    const machines = _machines?.machines || [];
    // console.log(machines)

    const [showFilters, setShowFilters] = useState(false);

    const counts = additionalData?.counts || { total: 0, online: 0, offline: 0 };

    const filterFields = [
        {
            key: 'machineId',
            label: 'Machine ID',
            type: 'text',
            placeholder: 'Search by machine ID...'
        },
        {
            key: 'name',
            label: 'Machine Name',
            type: 'text',
            placeholder: 'Search by name...'
        },
        {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
                // { value: '', label: 'All Status' },
                { value: 'online', label: 'Online' },
                { value: 'offline', label: 'Offline' }
            ]
        }
    ];

    const renderEnvironmentData = (environment) => {
        if (!environment) return <span className="text-muted">No data</span>;

        return (
            <div className="small">
                <div className="d-flex align-items-center justify-content-center mb-1">
                    <i className="fas fa-thermometer-half text-info me-2"></i>
                    <span>{environment.temperature ?? 'N/A'}°C</span>
                </div>
                <div className="d-flex align-items-center justify-content-center">
                    <i className="fas fa-tint text-primary me-2"></i>
                    <span>{environment.humidity ?? 'N/A'}%</span>
                </div>
            </div>
        );
    };

    const renderRefrigerationData = (refrigeration) => {
        if (!refrigeration) return <span className="text-muted">No data</span>;

        return (
            <div className="small">
                <div className="d-flex align-items-center justify-content-center mb-1">
                    <Badge bg={refrigeration.coolingOn ? 'success' : 'secondary'} className="me-2">
                        <i className="fas fa-snowflake me-1"></i>
                        {refrigeration.coolingOn ? 'ON' : 'OFF'}
                    </Badge>
                </div>
                <div className="d-flex align-items-center justify-content-center">
                    <Badge bg={refrigeration.compressorActive ? 'info' : 'secondary'}>
                        <i className="fas fa-cog me-1"></i>
                        {refrigeration.compressorActive ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            </div>
        );
    };

    const renderLightingData = (lighting) => {
        if (!lighting) return <span className="text-muted">No data</span>;

        return (
            <Badge bg={lighting.lightOn ? 'warning' : 'secondary'}>
                <i className="fas fa-lightbulb me-1"></i>
                {lighting.lightOn ? 'ON' : 'OFF'}
            </Badge>
        );
    };

    const renderDispensingData = (dispensing) => {
        if (!dispensing?.summary?.length) return <span className="text-muted">No data</span>;

        const successCount = dispensing.summary.find(s => s._id === 'success')?.count || 0;

        return (
            <Badge bg="success" className="rounded-pill">
                <i className="fas fa-check-circle me-1"></i>
                {successCount} Success
            </Badge>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row>
                <Col md="12">
                    <Card className="shadow border-0 rounded-3 overflow-hidden">
                        <div style={{ background: GRADIENTS.primary }} className="text-white py-3 px-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="mb-0 fw-semibold" style={{ fontSize: 'clamp(1rem, 4vw, 1.5rem)' }}>
                                        <i className="fas fa-chart-line me-2"></i>
                                        <span className="d-none d-sm-inline">Machine Status Dashboard</span>
                                        <span className="d-inline d-sm-none">Status</span>
                                    </h4>
                                    <p className="mb-0 opacity-75 mt-1 d-none d-md-block" style={{ fontSize: '0.9rem' }}>
                                        Real-time monitoring of machine telemetry and status
                                    </p>
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        variant="light"
                                        size="sm"
                                        onClick={() => setShowFilters(!showFilters)}
                                        className="fw-medium d-flex align-items-center"
                                    >
                                        <i className="fas fa-filter me-1"></i>
                                        <span className="d-none d-md-inline">Filters</span>
                                        {Object.values(filters).filter(v => v).length > 0 && (
                                            <Badge bg="danger" className="ms-2">{Object.values(filters).filter(v => v).length}</Badge>
                                        )}
                                    </Button>
                                    <Button
                                        variant="light"
                                        size="sm"
                                        onClick={refresh}
                                        className="fw-medium"
                                    >
                                        <i className="fas fa-sync-alt me-1"></i>
                                        <span className="d-none d-md-inline">Refresh</span>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {showFilters && (
                            <div className="px-4 pt-3 border-bottom">
                                <TableFilter
                                    filterConfig={filterFields}
                                    filters={filters}
                                    onFilterChange={updateFilters}
                                    onClearFilters={clearFilters}
                                />
                            </div>
                        )}

                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading machine status...</p>
                                </div>
                            ) : machines.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="display-1 text-muted mb-3 opacity-50">
                                        <i className="fas fa-chart-line"></i>
                                    </div>
                                    <h4 className="text-muted mb-3">No Machine Data Found</h4>
                                    <p className="text-muted mb-4">No telemetry data available at the moment</p>
                                    <Button variant="primary" onClick={refresh} className="fw-medium">
                                        <i className="fas fa-sync-alt me-2"></i>
                                        Try Again
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    {/* Statistics Cards */}
                                    <div className="p-4 bg-light border-bottom">
                                        <Row className="g-3">
                                            <Col md={4}>
                                                <div className="stat-card">
                                                    <div className="d-flex align-items-center">
                                                        <div className="stat-icon bg-primary bg-opacity-10 text-primary rounded-circle p-3 me-3">
                                                            <i className="fas fa-robot fa-lg"></i>
                                                        </div>
                                                        <div>
                                                            <h3 className="fw-bold mb-0">{_machines?.globalStats.total}</h3>
                                                            <small className="text-muted">Total Machines</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-card">
                                                    <div className="d-flex align-items-center">
                                                        <div className="stat-icon bg-success bg-opacity-10 text-success rounded-circle p-3 me-3">
                                                            <i className="fas fa-signal fa-lg"></i>
                                                        </div>
                                                        <div>
                                                            <h3 className="fw-bold mb-0">{_machines?.globalStats.online}</h3>
                                                            <small className="text-muted">Online</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-card">
                                                    <div className="d-flex align-items-center">
                                                        <div className="stat-icon bg-danger bg-opacity-10 text-danger rounded-circle p-3 me-3">
                                                            <i className="fas fa-exclamation-circle fa-lg"></i>
                                                        </div>
                                                        <div>
                                                            <h3 className="fw-bold mb-0">{_machines?.globalStats.offline}</h3>
                                                            <small className="text-muted">Offline</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Machine Status Table */}
                                    <div className="table-responsive">
                                        <Table hover className="mb-0 align-middle machine-status-table">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="border-0 ps-4">Machine ID</th>
                                                    <th className="border-0">Name</th>
                                                    <th className="border-0 text-center">Environment</th>
                                                    <th className="border-0 text-center">Refrigeration</th>
                                                    <th className="border-0 text-center">Lighting</th>
                                                    {/* <th className="border-0 text-center">Dispensing</th> */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {machines.map((machine) => (
                                                    <tr key={machine.machineId} className="align-middle">
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center">
                                                                <div className="machine-icon bg-primary bg-opacity-10 text-primary rounded-circle p-2 me-3">
                                                                    <i className="fas fa-robot"></i>
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-0 fw-semibold">{machine.machineId}</h6>
                                                                    <Badge
                                                                        bg={machine.status === 'online' ? 'success' : 'danger'}
                                                                        className="rounded-pill mt-1"
                                                                        style={{ fontSize: '0.7rem' }}
                                                                    >
                                                                        <i className={`fas fa-circle me-1`} style={{ fontSize: '0.4rem' }}></i>
                                                                        {machine.status}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <h6 className="mb-0 fw-semibold">{machine.name || 'Unnamed Machine'}</h6>
                                                            {machine.location && (
                                                                <small className="text-muted">
                                                                    <i className="fas fa-map-marker-alt me-1"></i>
                                                                    {machine.location}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            {renderEnvironmentData(machine.environment)}
                                                        </td>
                                                        <td className="text-center">
                                                            {renderRefrigerationData(machine.refrigeration)}
                                                        </td>
                                                        <td className="text-center">
                                                            {renderLightingData(machine.lighting)}
                                                        </td>
                                                        {/* <td className="text-center">
                                                            {renderDispensingData(machine.dispensing)}
                                                        </td> */}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </Card.Body>

                        {machines.length > 0 && (
                            <Card.Footer className="bg-light border-0 py-3 px-4">
                               
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    totalItems={totalItems}
                                    pageSize={pageSize}
                                    onPageChange={goToPage}
                                    onNextPage={nextPage}
                                    onPrevPage={prevPage}
                                    onPageSizeChange={changePageSize}
                                />
                            </Card.Footer>
                        )}
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default MachineStatus;
